import { useState, useCallback, useRef, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import map from 'lodash/map';

import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import SearchFilterBar, {
  resolveQuickSearch,
  type QuickFiltersType,
} from 'src/components/SearchFilterBar';
import DataTable from 'src/components/DataTable';
import PageLayout from 'src/components/PageLayout';
import {
  useDeleteCandidate,
  useDownloadCandidates,
  useGetAllCandidates,
} from 'src/pages/CandidatesPage/api';
import usePersistentPageState from 'src/utils/hooks/usePersistentPageState';
import CheckButton from 'src/components/CheckButton';

import messages from './messages';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import CandidatesFilters from './components/CandidatesFilters';

import { Styled } from './index.styled';
import AdvancedFiltersButton from 'src/components/AdvancedFiltersButton';
import { AdvancedFiltersState } from 'src/components/AdvancedFilters';

export type CandidateStatusType = 'all' | 'active';

const navTabs: Array<{ value: CandidateStatusType; label: string }> = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'active',
    label: 'Active',
  },
];

const STORAGE_KEY = 'candidates-page-state';

export default function CandidatesPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<CandidateStatusType>(
    navTabs[0].value,
  );

  // Had to do this because the filters and params needs to be persisted
  const { filters, setFilters, params, setParams } = usePersistentPageState({
    storageKey: STORAGE_KEY,
  });
  const [candidatesFilters, setCandidatesFilters] = useState<
    AdvancedFiltersState[]
  >([]);

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);

  const { data: candidates, isFetching: isFetchingCandidates } =
    useGetAllCandidates(
      [
        ...candidatesFilters,
        ...resolveQuickSearch(filters),
        ...(activeTab === 'active'
          ? [{ name: 'section', value: activeTab }]
          : []),
      ],
      activeTab,
      params,
    );

  const { mutate: deleteCandidate, isPending: isDeletingCandidate } =
    useDeleteCandidate();

  const { refetch: downloadFile, isFetching: isDownloading } =
    useDownloadCandidates([
      ...candidatesFilters,
      ...(selected.length ? [{ name: 'user_uuids', value: selected }] : []),
    ]);

  const quickFilters: Array<QuickFiltersType> = useRef([
    {
      autoFocus: true,
      id: 'quick_search',
      type: 'text' as QuickFiltersType['type'],
      placeholder: formatMessage(messages.searchInputPlaceholder),
    },
  ]).current;

  const checkIfExist = useCallback(
    (uuid: string) => {
      return selected.findIndex((item) => item === uuid) !== -1;
    },
    [selected],
  );

  const isSelectAll = useMemo(() => {
    return (
      candidates?.data?.length !== 0 &&
      selected.length === candidates?.data?.length
    );
  }, [selected.length, candidates?.data?.length]);

  const handleSelectAll = useCallback(() => {
    if (isSelectAll) {
      setSelected([]);
    } else if (candidates?.data) {
      const ids = candidates.data?.map((item) => item.uuid);
      setSelected(ids);
    }
  }, [candidates?.data, isSelectAll]);

  const handleSelect = useCallback(
    (uuid: string) => {
      const index = selected.indexOf(uuid);

      const updated =
        index !== -1
          ? [...selected.slice(0, index), ...selected.slice(index + 1)]
          : [...selected, uuid];

      setSelected(updated);
    },
    [selected],
  );

  const handleDelete = useCallback((uuid: string) => {
    deleteCandidate({ uuid });
  }, []);

  const columns: Array<{
    id: keyof UCM.AllCandidatesType | string;
    label: string | React.ReactNode;
  }> = useMemo(() => {
    return [
      {
        id: 'name',
        label: (
          <Grid container gap={1}>
            <CheckButton active={isSelectAll} onChange={handleSelectAll} />
            <Typography
              variant="body1"
              fontWeight={(theme) => theme.typography.fontWeightMedium}
            >
              {formatMessage(messages.studentColumnLabel)}
            </Typography>
          </Grid>
        ),
        render: (_: unknown, item: UCM.AllCandidatesType) => {
          if (!item.id) return '-';
          const { id, firstName, lastName, uuid } = item;
          const isSelected = checkIfExist(uuid);
          return (
            <Grid gap={1} container flexDirection="row">
              <CheckButton
                active={isSelected}
                onChange={() => handleSelect(uuid)}
              />

              <Typography variant="inherit">
                {id} - {firstName} {lastName}
              </Typography>
            </Grid>
          );
        },
      },
      // TODO - wait for backend
      {
        id: 'rating',
        label: formatMessage(messages.ratingColumnLabel),
        render: (_: unknown) => <Typography variant="inherit">-</Typography>,
        align: 'center',
      },
      {
        id: 'contact',
        label: formatMessage(messages.contactColumnLabel),
        render: (_: unknown, item: UCM.AllCandidatesType) => {
          const { email, phoneNumbers } = item;
          return (
            <>
              <Typography variant="inherit">{email}</Typography>
              <Typography variant="inherit">
                {phoneNumbers.join(', ')}
              </Typography>
            </>
          );
        },
      },
      {
        id: 'languages',
        label: formatMessage(messages.languagesColumnLabel),
        align: 'center',
        render: (languages: Array<string>) => {
          if (!languages || !languages.length)
            return <Typography variant="inherit">-</Typography>;

          return (
            <Typography variant="inherit">{languages.join(', ')}</Typography>
          );
        },
      },
      {
        id: 'cities',
        label: formatMessage(messages.locationColumnLabel),
        align: 'center',
        render: (cities: Array<string> | null) => {
          if (!cities?.length)
            return <Typography variant="inherit">-</Typography>;

          if (cities.length > 1) {
            return (
              <Grid container alignItems="center" justifyContent="center">
                <Tooltip
                  arrow
                  title={map(cities, (city) => (
                    <div key={city}>{city}</div>
                  ))}
                >
                  <Grid container spacing={0.5} alignItems="center">
                    {formatMessage(messages.multipleCitiesLabel)}
                    <Styled.SmallInfoOutlinedIcon />
                  </Grid>
                </Tooltip>
              </Grid>
            );
          }
          return <Typography variant="inherit">{cities[0]}</Typography>;
        },
      },
      {
        id: 'profileCompletion',
        label: formatMessage(messages.profileCompletionColumnLabel),
        render: (_: unknown, item: UCM.AllCandidatesType) => (
          <Typography variant="inherit">{item.status.name ?? '-'}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'actions',
        label: '',
        width: 0,
        align: 'center',
        render: (_: unknown, item: UCM.AllCandidatesType) => (
          <ConfirmationDialog
            onConfirm={() => handleDelete(item.uuid)}
            message={formatMessage(messages.deleteStudentConfirmMessage)}
          >
            {(handleDialogOpen) => (
              <IconButton
                onClick={handleDialogOpen}
                disabled={isDeletingCandidate}
              >
                <DeleteForeverOutlined fontSize="small" />
              </IconButton>
            )}
          </ConfirmationDialog>
        ),
      },
    ];
  }, [isDeletingCandidate, isSelectAll, handleSelect, handleSelectAll]);

  // eslint-disable-next-line
  const handleSearch = useCallback((filters: Record<string, any>) => {
    setParams({ page: 1 });
    setFilters(filters);
  }, []);

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const handleActiveTabChange = useCallback((tab: CandidateStatusType) => {
    setActiveTab(tab);
    setParams({ page: 1 });
  }, []);

  const handleExport = useCallback(() => {
    void downloadFile();
  }, []);

  const handlePressRow = useCallback((item: UCM.AllCandidatesType | null) => {
    if (item?.uuid) {
      navigate(`/students/${item.uuid}`);
    }
  }, []);

  const handleFilterClick = useCallback(() => {
    setOpenFilter(!openFilter);
  }, [openFilter]);

  const handleFiltersSearch = useCallback((filters: AdvancedFiltersState[]) => {
    setParams({ page: 1 });
    setCandidatesFilters(filters);
    setOpenFilter(false);
  }, []);

  return (
    <PageLayout
      iconName="students"
      title={formatMessage(messages.title)}
      tabs={navTabs}
      onActiveTabChange={handleActiveTabChange}
      activeTab={activeTab}
    >
      <Grid container spacing={2}>
        <Grid container spacing={2} size={12} alignItems="center">
          <Grid size={3}>
            <SearchFilterBar
              fields={quickFilters}
              onSearch={handleSearch}
              variant="noBorder"
              initialValue={filters}
            />
          </Grid>
          <Grid size={1}>
            <AdvancedFiltersButton
              open={openFilter}
              filters={candidatesFilters}
              handleOpenFilters={handleFilterClick}
            />
          </Grid>
          <Grid
            size={8}
            container
            alignItems="center"
            justifyContent="flex-end"
          >
            <ConfirmationDialog
              onConfirm={handleExport}
              message={formatMessage(messages.exportConfirmMessage)}
            >
              {(handleDialogOpen) => (
                <Button
                  variant="outlined"
                  onClick={handleDialogOpen}
                  startIcon={<FileDownloadOutlinedIcon />}
                >
                  {formatMessage(messages.exportButtonLabel)}
                </Button>
              )}
            </ConfirmationDialog>
          </Grid>
        </Grid>
        <CandidatesFilters
          open={openFilter}
          handleSearch={handleFiltersSearch}
          filters={candidatesFilters}
        />
        <Grid size={12}>
          <DataTable
            rows={candidates?.data}
            pagination={candidates?.meta}
            columns={columns}
            onPaginate={handlePagination}
            loading={isFetchingCandidates || isDownloading}
            pressRow={handlePressRow}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
}
